<template>
  <v-container id="user-profile" tag="section">
    <v-row justify="center">
      <v-col cols="12" md="6">
        <v-row>
          <v-col cols="12" v-if="user !== null && user !== undefined">
            <base-material-card
              class="v-card-profile"
              :avatar="user.photoURL !== null ? user.photoURL : placeholderAvatar"
            >
              <v-card-text class="text-center">
                <v-chip
                  class="ma-2"
                  color="cyan darken-3"
                  text-color="white"
                  label
                  v-if="accountType.length > 0"
                >
                  <v-icon left>mdi-badge-account</v-icon>
                  {{accountType}}
                </v-chip>

                <v-chip
                  class="ma-2"
                  color="indigo darken-3"
                  text-color="white"
                  label
                  v-if="role.length > 0"
                >
                  <v-icon left>mdi-account-tie</v-icon>
                  {{role}}
                </v-chip>

                <h4 class="display-2 font-weight-bold mb-3 black--text">{{displayName}}</h4>

                <p class="font-weight-bold black--text">{{user.phoneNumber}}</p>

                <p class="font-weight-bold black--text">{{user.email}}</p>

                <div class="text-center">
                  <v-btn
                    color="error"
                    rounded
                    outlined
                    class="text-capitalize font-weight-bold ma-2"
                    v-if="!user.emailVerified"
                    @click="verifyEmail"
                  >
                    <v-icon left>mdi-email</v-icon>Verify Your Email
                  </v-btn>

                  <v-btn
                    class="text-capitalize font-weight-bold ma-2"
                    rounded
                    outlined
                    color="info"
                    @click="changeDisplayName"
                  >
                    <v-icon left>mdi-pencil</v-icon>Change Name
                  </v-btn>

                  <v-btn
                    class="text-capitalize font-weight-bold ma-2"
                    rounded
                    outlined
                    color="cyan"
                    @click="changeProfilePhoto"
                  >
                    <v-icon left>mdi-camera</v-icon>Change Photo
                  </v-btn>
                </div>
              </v-card-text>
            </base-material-card>
          </v-col>

          <v-col cols="12">
            <base-material-card
              icon="mdi-account-outline"
              color="accent"
              v-if="changingDisplayName"
            >
              <template v-slot:after-heading>
                <div class="font-weight-light card-title">
                  Update Profile
                  <span class="body-1">— Change your display name</span>
                </div>
              </template>

              <v-card-text class="text-center">
                <validation-observer v-slot="{ handleSubmit }">
                  <form @submit.prevent="handleSubmit(validateForm)">
                    <validation-provider v-slot="{ errors }" name="Profile Name" rules="required">
                      <v-text-field
                        v-model="profileName"
                        :error-messages="errors"
                        color="secondary"
                        type="text"
                        label="Display Name..."
                        prepend-icon="mdi-account"
                        class="mt-10"
                      />
                    </validation-provider>

                    <div class="text-center">
                      <v-btn
                        color="primary"
                        rounded
                        class="text-capitalize font-weight-bold ma-2"
                        type="submit"
                        :disabled="loading"
                        :loading="loading"
                      >Change Your Display Name</v-btn>

                      <v-btn
                        class="text-capitalize font-weight-bold ma-2"
                        rounded
                        outlined
                        default
                        @click="clearForm"
                      >
                        <v-icon left>mdi-delete</v-icon>Cancel
                      </v-btn>
                    </div>
                  </form>
                </validation-observer>
              </v-card-text>
            </base-material-card>
          </v-col>

          <v-col cols="12">
            <base-material-card icon="mdi-camera" color="accent" v-if="changingProfilePhoto">
              <template v-slot:after-heading>
                <div class="font-weight-light card-title">
                  Update Profile
                  <span class="body-1">— Change your profile photo</span>
                </div>
              </template>

              <v-card-text class="text-center">
                <v-row>
                  <v-col cols="12">
                    <v-layout align-center justify-center>
                      <v-flex>
                        <img :src="profileImageUrl" height="150" v-if="profileImageUrl" />
                        <v-text-field
                          label="Select Profile Photo:"
                          @click="pickFile"
                          v-model="profileImageName"
                          prepend-icon="mdi-camera"
                        ></v-text-field>
                        <input
                          type="file"
                          style="display: none"
                          ref="image"
                          accept="image/png, image/jpeg, image/jpg"
                          @change="onFilePicked"
                        />

                        <div class="text-center">
                          <v-btn
                            color="primary"
                            rounded
                            class="text-capitalize font-weight-bold mr-0"
                            @click="upload"
                          >Change Profile Photo</v-btn>

                          <v-btn
                            class="text-capitalize font-weight-bold ma-2"
                            rounded
                            outlined
                            default
                            @click="clearForm"
                          >
                            <v-icon left>mdi-delete</v-icon>Cancel
                          </v-btn>
                        </div>
                      </v-flex>
                    </v-layout>
                  </v-col>
                </v-row>
              </v-card-text>
            </base-material-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "ProfilePage",
  data() {
    return {
      loading: false,
      placeholderAvatar: require("@/assets/avatar.png"),
      rules: [
        value =>
          !value ||
          value.size < 1000000 ||
          "Avatar size should be less than 1 MB!"
      ],
      changingDisplayName: false,
      profileName: null,
      changingProfilePhoto: false,
      profileImage: null,
      profileImageUrl: null,
      profileImageName: null,
      profileImageType: null
    };
  },
  $_veeValidate: {
    validator: "new"
  },
  created() {
    
  },
  computed: {
    ...mapGetters("userAccountService", {
      accountType: "accountType",
      role: "role"
    }),
    ...mapGetters("authenticationService", {
      user: "user",
      displayName: "displayName",
      token: "token"
    })
  },
  methods: {
    changeDisplayName: function() {
      this.clearForm();
      this.changingDisplayName = true;
    },
    changeProfilePhoto: function() {
      this.clearForm();
      this.changingProfilePhoto = true;
    },
    pickFile() {
      this.$refs.image.click();
    },

    onFilePicked(e) {
      const files = e.target.files;
      if (files[0] !== undefined) {
        this.profileImageName = files[0].name;
        if (this.profileImageName.lastIndexOf(".") <= 0) {
          return;
        }
        //console.log(files[0].type);
        this.profileImageType = files[0].type;
        const fr = new FileReader();
        fr.readAsDataURL(files[0]);
        fr.addEventListener("load", () => {
          this.profileImageUrl = fr.result;
          this.profileImage = files[0];
        });
      } else {
        this.profileImageName = null;
        this.profileImage = null;
        this.profileImageUrl = null;
        this.profileImageType = null;
      }
    },
    
    clearForm() {
      this.profileName = this.displayName;
      this.profileImageName = null;
      this.profileImage = null;
      this.profileImageUrl = null;
      this.changingDisplayName = false;
      this.changingProfilePhoto = false;
    },
    validateForm(scope) {},
    verifyEmail() {},
    upload() {}
  },  
};
</script>
